import api from './api';

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface UserPrivilegeInfo {
  id: number;
  name: string;
  manage_users: boolean;
  all_patients: boolean;
  questionnaires: boolean;
  email_and_sms_templates: boolean;
  config: boolean;
  config_sync: boolean;
  wellness: boolean;
  doc_assistant?: boolean;
}

export interface UserInfo {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: 'admin' | 'therapist';
  tenant_id: number | null;
  timezone: string;
  is_2fa_enabled: boolean;
  // The coordinator's district, e.g. "דן". The backend derives a new lead's
  // sub_clinic from this, so it is never chosen in the form.
  sub_clinic?: string | null;
  privilege_id: number | null;
  user_privilege: UserPrivilegeInfo | null;
}

export interface Enable2FAResponse {
  qr_code: string;
  backup_codes: string[];
}

// Identifies this client to the shared psy-clinic backend, which serves several
// front ends from the same /auth/login endpoint.
export const APP_NAME = 'hmo';

export const authService = {
  async login(email: string, password: string, totpToken?: string): Promise<LoginResponse> {
    const response = await api.post('/auth/login', {
      email,
      password,
      totp_token: totpToken,
      app: APP_NAME,
    });
    return response.data;
  },

  async loginWithGoogle(credential: string, totpToken?: string): Promise<LoginResponse> {
    const response = await api.post('/auth/google', { credential, totp_token: totpToken });
    return response.data;
  },

  async getCurrentUser(): Promise<UserInfo> {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async setup2FA(): Promise<Enable2FAResponse> {
    const response = await api.post('/auth/setup-2fa');
    return response.data;
  },

  async enable2FA(totpToken: string): Promise<void> {
    await api.post('/auth/enable-2fa', {
      totp_token: totpToken,
    });
  },

  async disable2FA(totpToken: string): Promise<void> {
    await api.post('/auth/disable-2fa', {
      totp_token: totpToken,
    });
  },

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const response = await api.post('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email });
  },

  async resetPassword(key: string, newPassword: string): Promise<void> {
    await api.post('/auth/reset-password', {
      key,
      new_password: newPassword,
    });
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
  },
};